import { Chip } from '@mui/material';
import { isPlainObject, keys, uniq } from 'lodash';
import PropTypes from 'prop-types';

// POM-6552: the ticket's colours. Any environment can override them one shipping option at a
// time through REACT_APP_SHIPPING_OPTION_CHIP_COLORS, a JSON object in this same shape (e.g.
// {"NEXT_DAY":{"bg":"#FFD600"}}); whatever it leaves out keeps these defaults.
export const DEFAULT_SHIPPING_OPTION_CHIP_COLORS = {
  NEXT_DAY: { bg: '#FFEB3B', text: '#000000' },
  TWO_DAY: { bg: '#000000', text: '#FFFFFF' },
  USPS_PRIORITY_MAIL: { bg: '#B3E5FC', text: '#000000' },
  USPS_PRIORITY_MAIL_EXPRESS: { bg: '#B3E5FC', text: '#000000' },
  USPS_GROUND_ADVANTAGE: { bg: '#B3E5FC', text: '#000000' },
};

// The value arrives as a build-time string, so it is parsed and guarded: invalid JSON or a
// non-object falls back to the defaults rather than breaking the queue.
const parseOverrides = (raw) => {
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw);
    return isPlainObject(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

export const resolveShippingOptionChipColors = (raw) => {
  const overrides = parseOverrides(raw);
  const optionCodes = uniq([...keys(DEFAULT_SHIPPING_OPTION_CHIP_COLORS), ...keys(overrides)]);

  return Object.fromEntries(
    optionCodes.map((optionCode) => [
      optionCode,
      {
        ...DEFAULT_SHIPPING_OPTION_CHIP_COLORS[optionCode],
        ...(isPlainObject(overrides[optionCode]) ? overrides[optionCode] : {}),
      },
    ])
  );
};

const SHIPPING_OPTION_CHIP_COLORS = resolveShippingOptionChipColors(
  process.env.REACT_APP_SHIPPING_OPTION_CHIP_COLORS
);

// sx colours, not the MUI `color` prop: the theme maps `warning` to navy. An option with no
// colours configured renders as the plain default chip.
const ShippingOptionChip = ({ optionCode, label }) => {
  const colors = SHIPPING_OPTION_CHIP_COLORS[optionCode];

  return (
    <Chip
      label={label}
      size='small'
      data-testid={`shipping-option-${optionCode}`}
      sx={{
        fontWeight: 500,
        ...(colors?.bg && { bgcolor: colors.bg }),
        ...(colors?.text && { color: colors.text }),
      }}
    />
  );
};

ShippingOptionChip.propTypes = {
  optionCode: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
};

export default ShippingOptionChip;
